import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { IProductHeader } from '~/Slices/schema';
import { productHeaderId } from '~/Slices/schema';
import type { AppThunk, IAppState, ILoader } from '~/appstate';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import type {
	GetGlAccountsForGlGroupResult,
	GlAccountAndDescription,
} from '~/ogm-hub';
import { GeneralLedgerController } from '~/ogm-hub';
import type { IGLAccount } from '~/values';
import { fetchGlGroups, selectGlGroupById } from '../GlGroupsSlice';
import { setGlGroup } from '../ProductHeadersSlice';
import { metaProductPageSlice, selectSupplierId } from './MetaProductPageSlice';

export interface IGlAssignmentModalState {
	productHeader: IProductHeader;
	selectedGlGroupId: number;
	glAccounts: ILoader<IGLAccount[]>;
	processingAssign: boolean;
}

export const initialState: IGlAssignmentModalState = {
	productHeader: null,
	selectedGlGroupId: 0,
	glAccounts: {
		error: false,
		inProcess: false,
		payload: null,
	},
	processingAssign: false,
};

export const fetchGlAccounts = createAsyncThunk<
	GlAccountAndDescription[],
	number,
	{ rejectValue: string[] | Response }
>(
	'metaProductPage/glAssignmentModal/fetchGlAccounts',
	async (glGroupId, { rejectWithValue }) => {
		if (!glGroupId) {
			return [];
		}

		try {
			const result = (await unwrapFetch2(
				GeneralLedgerController.GetGlAccountsForGlGroup(glGroupId, 0, 100000)
			)) as GetGlAccountsForGlGroupResult;
			return result.accounts;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const assignGlAccount = createAsyncThunk<
	void,
	{ glGroupId: number; productNumber: string; supplierId: number },
	{ rejectValue: string[] | Response }
>(
	'metaProductPage/glAssignmentModal/assignGlAccount',
	async ({ glGroupId, productNumber, supplierId }, { rejectWithValue }) => {
		try {
			await unwrapFetch2(
				GeneralLedgerController.AssignGlAccount({
					glGroupId,
					productNumber,
					supplierId,
				})
			);
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const open =
	(productHeader: IProductHeader): AppThunk =>
	(dispatch) => {
		dispatch(glAssignmentModalSlice.actions.setProduct({ productHeader }));
		dispatch(metaProductPageSlice.actions.showGlAssignmentModal());
		dispatch(fetchGlGroups());
		dispatch(fetchGlAccounts(productHeader.glGroup?.groupId));
	};

export const changeSelection =
	(glGroupId: number): AppThunk =>
	(dispatch) => {
		dispatch(glAssignmentModalSlice.actions.setSelected(glGroupId));
		dispatch(fetchGlAccounts(glGroupId));
	};

export const submit = createAsyncThunk<void, void, { state: IAppState }>(
	'metaProductPage/glAssignmentModal/submit',
	async (args, { dispatch, getState }) => {
		const state = getState();
		const { selectedGlGroupId, productHeader } =
			state.metaProductPage.glAssignmentModal;

		await dispatch(
			assignGlAccount({
				glGroupId: selectedGlGroupId,
				productNumber: productHeader.supplierProductNumber,
				supplierId: selectSupplierId(state),
			})
		);

		dispatch(metaProductPageSlice.actions.hideGlAssignmentModal());
		dispatch(
			setGlGroup({
				id: productHeaderId(productHeader),
				glGroup: selectGlGroupById(state, selectedGlGroupId),
			})
		);
	}
);

export const glAssignmentModalSlice = createSlice({
	name: 'metaProductPage/glAssignmentModal',
	initialState,
	reducers: {
		setProduct: (state, action) => {
			state.productHeader = action.payload.productHeader;
			state.selectedGlGroupId = action.payload.productHeader.glGroup?.groupId;
		},
		setSelected: (state, action) => {
			state.selectedGlGroupId = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchGlAccounts.rejected, (state) => {
			state.glAccounts.error = true;
		});
		builder.addCase(fetchGlAccounts.pending, (state) => {
			state.glAccounts.error = false;
			state.glAccounts.inProcess = true;
		});
		builder.addCase(fetchGlAccounts.fulfilled, (state, action) => {
			state.glAccounts.error = false;
			state.glAccounts.inProcess = false;
			state.glAccounts.payload = action.payload;
		});
		builder.addCase(submit.rejected, (state) => {
			state.processingAssign = false;
		});
		builder.addCase(submit.pending, (state) => {
			state.processingAssign = true;
		});
		builder.addCase(submit.fulfilled, (state) => {
			state.processingAssign = false;
		});
	},
});

export const { setProduct, setSelected } = glAssignmentModalSlice.actions;
